import { useCallback, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { adminApi } from "../api/admin";
import type { IndexResult } from "../api/types";
import { useAdminAuth } from "./AdminAuthContext";

const TABS = [
  { to: "/admin/photos", label: "Photos" },
  { to: "/admin/videos", label: "Videos" },
  { to: "/admin/front-page", label: "Front page" },
  { to: "/admin/members", label: "Members" },
  { to: "/admin/blog", label: "Blog" },
  { to: "/admin/documents", label: "Documents" },
  { to: "/admin/comments", label: "Comments" },
];

/** Shared by the nested admin pages so a re-index can force them to refetch. */
export interface AdminOutletContext {
  refreshToken: number;
}

/**
 * Admin chrome: a title bar with the re-index and sign-out actions, the section
 * tabs, and the routed table. "Re-index from ./DATA/INPUT" reconciles the
 * database with the content files and bumps a refresh token so the visible
 * table reloads with whatever changed on disk.
 */
export function AdminLayout() {
  const { logout } = useAdminAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<IndexResult | null>(null);
  const [error, setError] = useState("");
  const [refreshToken, setRefreshToken] = useState(0);

  const reindex = useCallback(async () => {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const r = await adminApi.reindex();
      setResult(r);
      setRefreshToken((t) => t + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Re-index failed.");
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <div className="admin">
      <div className="admin__bar">
        <h1>Admin console</h1>
        <span className="grow" />
        <button className="btn btn--ghost" onClick={reindex} disabled={busy}>
          {busy ? "Indexing…" : "Re-index from ./DATA/INPUT"}
        </button>
        <button className="btn" onClick={logout}>
          Sign out
        </button>
      </div>

      {error && (
        <p className="notice notice--err" role="alert">
          ✕ {error}
        </p>
      )}
      {result && (
        <p className="notice notice--ok" role="status">
          {result.skipped
            ? `No input directory found at ${result.inputDir} — nothing indexed.`
            : `Indexed ${result.inputDir}: ${result.created} created, ${result.updated} updated, ${result.unchanged} unchanged, ${result.deleted} deleted` +
              (result.errors.length ? `, ${result.errors.length} error(s).` : ".")}
        </p>
      )}
      {result && result.errors.length > 0 && (
        <ul className="notice notice--err" style={{ marginTop: "-0.5rem" }}>
          {result.errors.map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
        </ul>
      )}

      <nav className="admin__tabs">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) => "admin__tab" + (isActive ? " active" : "")}
          >
            {t.label}
          </NavLink>
        ))}
      </nav>

      <Outlet context={{ refreshToken } satisfies AdminOutletContext} />
    </div>
  );
}
